import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Stack, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

const STATUS_COLOR = { draft: 'default', trial: 'info', active: 'success', unpublished: 'warning', suspended: 'error' };

/**
 * Status and the primary actions. The first publish is "Julkaise ilmaiseksi 30 päiväksi";
 * every later publish is plain "Julkaise". Unpublish asks for confirmation.
 */
export default function PublishBar({ website, hasContent, busy, onPublish, onUnpublish, onShowVersions, onGenerate }) {
  const { t } = useTranslation();
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const status = website?.status || 'draft';
  const live = status === 'trial' || status === 'active';
  const firstPublish = !website?.trial_ends_at && !live;
  const included = website?.included;

  return (
    <Box data-testid="website-publish-bar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
      <Chip label={t(`website.status.${status}`)} color={STATUS_COLOR[status] || 'default'} data-testid="website-status" />
      {website?.hostname && (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {live ? (
            <Link href={website.public_url} target="_blank" rel="noopener" data-testid="website-public-link">
              {website.hostname} <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          ) : (
            <span data-testid="website-hostname">{website.hostname}</span>
          )}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onShowVersions} disabled={busy} data-testid="website-versions">{t('website.versions')}</Button>
        <Button onClick={onGenerate} disabled={busy || !hasContent} data-testid="website-regenerate-all">{t('website.regenerate_all')}</Button>
        {live && (
          <Button color="warning" onClick={() => setConfirmUnpublish(true)} disabled={busy} data-testid="website-unpublish">
            {t('website.unpublish')}
          </Button>
        )}
        <Button variant="contained" onClick={onPublish} disabled={busy || !hasContent || status === 'suspended'} data-testid="website-publish">
          {firstPublish && !included ? t('website.publish_trial') : t('website.publish')}
        </Button>
      </Stack>

      <Dialog open={confirmUnpublish} onClose={() => setConfirmUnpublish(false)}>
        <DialogTitle>{t('website.unpublish_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('website.unpublish_confirm_text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnpublish(false)}>{t('website.cancel')}</Button>
          <Button color="warning" variant="contained" onClick={() => { setConfirmUnpublish(false); onUnpublish(); }} data-testid="website-unpublish-confirm">
            {t('website.unpublish')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
